import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { getFundTheses, getFunds } from '../services/api';
import FundThesisView from '../components/InvestmentThesis/FundThesisView';
import { InvestmentThesis as InvestmentThesisType } from '../types';
import { useErrorDialog } from '../contexts/ErrorDialogContext';

const FundThesisEditor: React.FC = () => {
  const { fundId } = useParams<{ fundId: string }>();
  const navigate = useNavigate();
  const [fund, setFund] = useState<{ id: string; name: string } | null>(null);
  const [fundThesis, setFundThesis] = useState<InvestmentThesisType | null>(null);
  const [loading, setLoading] = useState(true);
  const { showErrorDialog, showSuccessDialog } = useErrorDialog();

  useEffect(() => {
    const fetchData = async () => {
      if (!fundId) {
        navigate('/investment-thesis');
        return;
      }

      setLoading(true);
      try {
        // In a real app, you'd likely have a getFund(fundId) endpoint.
        // Here, we'll fetch all funds and find the one we need.
        // This is not ideal for performance but works with the current API.
        const fundsResponse = await getFunds({});
        const currentFund = fundsResponse.funds.find((f: any) => f.id === fundId);

        if (!currentFund) {
          throw new Error('Fund not found');
        }
        setFund({ id: currentFund.id, name: currentFund.name });

        // Fetch the specific thesis for this fund
        const fundThesesResponse = await getFundTheses();
        const currentThesis = fundThesesResponse.find((ft: any) => ft.fund_id === fundId);
        setFundThesis(currentThesis || null);

      
            } catch (error: any) {
              console.error('Failed to fetch fund thesis data:', error);
              showErrorDialog(error.message || 'Failed to fetch fund thesis data.');
              navigate('/investment-thesis');
            } finally {
              setLoading(false);
            }
          };
      
          fetchData();
        }, [fundId, navigate, showErrorDialog]);
      
        const handleThesisAction = async (action: () => Promise<any>, successMessage: string) => {
          try {
            await action();
            await refetchData(); // Refetch data after successful action
            showSuccessDialog(successMessage);
          } catch (error: any) {
            console.error('Fund thesis action failed:', error);
            showErrorDialog(error.message || 'An unexpected error occurred during fund thesis action.');
          }
        };
      
        const refetchData = async () => {
          try {
            const fundThesesResponse = await getFundTheses();
            const currentThesis = fundThesesResponse.find((ft: any) => ft.fund_id === fundId);
            setFundThesis(currentThesis || null);
          } catch (error: any) {
            console.error('Failed to refetch fund thesis:', error);
            showErrorDialog(error.message || 'Failed to refetch fund thesis.');
          }
        };
      
        const handleBack = () => {
          navigate('/investment-thesis');
        };
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ mb: 2, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">
          {fundThesis ? 'Edit' : 'Create'} Investment Thesis for {fund?.name}
        </Typography>
        <Button startIcon={<BackIcon />} onClick={handleBack} variant="outlined">
          Back to Thesis List
        </Button>
      </Paper>
      <FundThesisView
        fund={fund}
        fundThesis={fundThesis}
        onBack={handleBack}
        onSave={(action: () => Promise<any>) => handleThesisAction(action, 'Fund thesis saved successfully!')}
        onRollback={(action: () => Promise<any>) => handleThesisAction(action, 'Fund thesis rolled back successfully!')}
      />
    </Box>
  );
};

export default FundThesisEditor;